import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const utahData = "/img/headerlogo.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";
const pdfIcon = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

const getAgeLabel = (value) => {
  const map = {
    A00: "All Ages (14-99)",
    A01: "14-18",
    A02: "19-21",
    A03: "22-24",
    A04: "25-34",
    A05: "35-44",
    A06: "45-54",
    A07: "55-64",
    A08: "65-99",
  };
  return map[value] || "Unknown";
};

const AgeGroupEmployeeChart = ({ data }) => {
  const pdfRef = useRef();
  const safeData = data ?? [];

  if (!safeData.length) {
    return <p>No data available</p>;
  }

  const years = Object.keys(safeData[0]).filter((key) =>
    key.startsWith("year_"),
  );

  const latestYear = years[years.length - 1];

  // =========================
  // Summary Calculations
  // =========================
  const totalEmployees = safeData.reduce((sum, row) => {
    return (
      sum +
      years.reduce((rowSum, year) => rowSum + (parseInt(row[year]) || 0), 0)
    );
  }, 0);

  const highestAverage = safeData.reduce(
    (max, row) =>
      parseInt(row.average || 0) > parseInt(max.average || 0) ? row : max,
    safeData[0],
  );

  // =========================
  // Chart Data
  // =========================
  const colorPalette = [
    "#014CB2",
    "#EC4899",
    "#22c55e",
    "#f59e0b",
    "#8b5cf6",
    "#ef4444",
    "#10b981",
    "#6366f1",
  ];

  const chartData = {
    labels: years.map((year) => year.replace("year_", "")),
    datasets: safeData.map((item, index) => ({
      label: getAgeLabel(item.agegrp_label),
      data: years.map((year) => parseInt(item[year] || 0)),
      backgroundColor: colorPalette[index % colorPalette.length],
      borderRadius: 6,
    })),
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index" },
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "📊 Employee Distribution by Age Group (2018–2024)",
        font: { size: 16, weight: "bold" },
      },
      tooltip: {
        callbacks: {
          label: (context) =>
            `${context.dataset.label}: ${context.parsed.y.toLocaleString()}`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        title: { display: true, text: "Year" },
      },
      y: {
        beginAtZero: true,
        grid: { color: "#f1f5f9" },
        title: { display: true, text: "Employees" },
        ticks: {
          callback: (value) => value.toLocaleString(),
        },
      },
    },
  };

  // =========================
  // PDF Export
  // =========================
  const downloadPDF = () => {
    const input = pdfRef.current;

    html2canvas(input, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      const pageWidth = pdf.internal.pageSize.getWidth();
      const imgWidth = pageWidth - 10;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      pdf.addImage(utahData, "PNG", 60, 10, 32, 16);
      pdf.setFontSize(26);
      pdf.text("UTahData.org", 95, 22);
      pdf.line(10, 35, 200, 35);

      pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);

      pdf.line(10, 260, 200, 260);
      const footerY = 265;
      pdf.addImage(utahData, "PNG", 22, footerY, 30, 15);
      pdf.setFontSize(10);
      pdf.text("Built by", 55, footerY + 10);
      pdf.addImage(bodex, "PNG", 68, footerY + 4, 44, 10);
      pdf.text("using", 112, footerY + 10);
      pdf.addImage(kpi, "PNG", 125, footerY, 20, 20);

      pdf.text(`Downloaded on: ${new Date().toLocaleDateString()}`, 150, 295);
      pdf.text(`From: ${window.location.href}`, 10, 295);

      pdf.save("age_group_employee_analysis.pdf");
    });
  };

  return (
    <>
      <div ref={pdfRef} className="p-8 bg-white rounded-2xl shadow-xl">
        <h2 className="text-2xl font-bold mb-8 text-center text-gray-800">
          Employee Data by Age Group
        </h2>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 text-center">
          <div className="p-5 bg-blue-50 rounded-xl shadow">
            <p className="text-sm font-medium text-gray-500">Latest Year</p>
            <p className="text-2xl font-bold text-blue-700">
              {latestYear.replace("year_", "")}
            </p>
          </div>

          <div className="p-5 bg-green-50 rounded-xl shadow">
            <p className="text-sm font-medium text-gray-500">
              Total Employees (All Years)
            </p>
            <p className="text-2xl font-bold text-green-700">
              {totalEmployees.toLocaleString()}
            </p>
          </div>

          <div className="p-5 bg-yellow-50 rounded-xl shadow">
            <p className="text-sm font-medium text-gray-500">Highest Average</p>
            <p className="text-2xl font-bold text-yellow-600">
              {getAgeLabel(highestAverage.agegrp_label)}
            </p>
          </div>
        </div>

        {/* Table + Chart */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
              <thead className="bg-gray-800 text-white">
                <tr>
                  <th className="p-3">Age Group</th>
                  {years.map((year) => (
                    <th key={year} className="p-3">
                      {year.replace("year_", "")}
                    </th>
                  ))}
                  <th className="p-3">Average</th>
                </tr>
              </thead>
              <tbody>
                {safeData.map((row) => (
                  <tr
                    key={row.agegrp_label}
                    className={`hover:bg-gray-50 ${
                      row.agegrp_label === highestAverage.agegrp_label
                        ? "bg-yellow-50 font-semibold"
                        : ""
                    }`}
                  >
                    <td className="p-3">{getAgeLabel(row.agegrp_label)}</td>
                    {years.map((year) => (
                      <td key={year} className="p-3">
                        {Number(row[year] || 0).toLocaleString()}
                      </td>
                    ))}
                    <td className="p-3">
                      {Number(row.average || 0).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-xl shadow p-6 h-96">
            <Bar data={chartData} options={options} />
          </div>
        </div>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-6">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-6 py-3 text-white rounded-xl flex items-center gap-3 shadow-lg transition-all hover:scale-105"
        >
          <span className="font-medium">Download as PDF</span>
          <img className="w-6" src={pdfIcon} alt="pdf" />
        </button>
      </div>
    </>
  );
};

export default AgeGroupEmployeeChart;
